import React from 'react';
import { Link } from 'react-router-dom';
import { expertCounsellors } from '../data/mock';
import { GraduationCap, Award, Video, Calendar, MessageCircle, ArrowRight } from 'lucide-react';
import { useMobileMarqueeLoop } from '../hooks/useMobileMarqueeLoop';
import { useMediaQuery } from '../hooks/useMediaQuery';

const MARQUEE_CARD =
  'flex-shrink-0 box-border min-w-0 max-w-full w-[min(24rem,calc(100vw-2.25rem))] snap-center';

function CounsellorCardBody({ counsellor }) {
  return (
    <div className="group box-border w-full max-w-full bg-white rounded-3xl overflow-hidden border-2 border-gray-200 hover:border-blue-400 transition-all duration-300 hover:shadow-2xl md:transform md:hover:-translate-y-2 h-full">
      <div className="relative h-72 sm:h-80 overflow-hidden max-w-full isolate">
        <img
          src={counsellor.image}
          alt={counsellor.name}
          width={400}
          height={320}
          loading="lazy"
          decoding="async"
          className="relative z-0 w-full h-full max-w-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 z-[1] bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />

        <div className="absolute top-2 right-2 z-10 bg-white/95 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full shadow-md ring-1 ring-white/70">
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-blue-600 shrink-0" />
            <span className="text-sm font-bold text-gray-900">{counsellor.experience}</span>
          </div>
        </div>
      </div>

      <div className="p-6 sm:p-8 max-w-full overflow-hidden">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 break-words">{counsellor.name}</h3>
        <p className="text-base sm:text-lg font-semibold text-blue-600 mb-4 break-words">{counsellor.role}</p>
        <p className="text-gray-600 leading-relaxed break-words">{counsellor.expertise}</p>
      </div>
    </div>
  );
}

function CounsellorsMobileMarquee() {
  const marqueeRef = useMobileMarqueeLoop({ direction: -1, speed: 0.4 });

  return (
    <div className="relative">
      <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-blue-50 to-transparent z-10" />
      <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-blue-50 to-transparent z-10" />

      <div ref={marqueeRef} className="counsellors-scroll snap-x snap-mandatory w-full max-w-full">
        <div className="counsellors-track md:gap-0">
          {[...expertCounsellors, ...expertCounsellors].map((counsellor, index) => (
            <div key={`${counsellor.id}-${index}`} className={`counsellor-card ${MARQUEE_CARD}`}>
              <CounsellorCardBody counsellor={counsellor} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const ExpertCounsellorsSection = () => {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  return (
    <section
      className="py-24 bg-gradient-to-br from-blue-50 via-white to-teal-50 overflow-hidden"
      aria-labelledby="expert-counsellors-heading"
    >
      <div className="container mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-100 px-4 py-2 rounded-full mb-6">
            <GraduationCap className="w-5 h-5 text-blue-700" />
            <span className="text-sm font-semibold text-blue-900">Real Expert Guidance</span>
          </div>

          <h2 className="font-display text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
            This isn&apos;t typical career counseling..
          </h2>
          <div className="text-xl text-gray-600 max-w-3xl mx-auto space-y-4 text-left">
            <p>
              Most students think career counseling is generic advice or a waste of time, and honestly,
              they&apos;re not wrong.
            </p>
            <p>
              That&apos;s exactly why our sessions are different. You come in with your personalized
              report, and we help you turn it into a clear, practical action plan tailored to you.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mt-12 text-left">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Video className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Live Video Sessions</h3>
                <p className="text-gray-600 text-base">Talk to real experts who actually understand your profile</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Flexible Scheduling</h3>
                <p className="text-gray-600 text-base">Book sessions when they fit your school week and family time</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Next steps, written</h3>
                <p className="text-gray-600 text-base">Leave with tasks, not pep talk</p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center max-w-3xl mx-auto mb-12">
          <h3 id="expert-counsellors-heading" className="font-display text-3xl lg:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
            Meet Our Expert Counsellors
          </h3>
          <p className="text-lg text-gray-600">
            Practitioners who turn your Career DNA report into decisions you can act on, with empathy and rigor.
          </p>
        </div>

        {isDesktop ? (
          <div className="relative -mx-6 max-w-none">
            <div
              className="flex gap-8 overflow-x-auto overflow-y-hidden pb-2 px-6 snap-x snap-mandatory scroll-smooth overscroll-x-contain [scrollbar-width:thin]"
              style={{ scrollPaddingInline: '1.5rem' }}
            >
              {expertCounsellors.map((counsellor) => (
                <div
                  key={counsellor.id}
                  className="min-h-0 flex-shrink-0 w-[min(22rem,calc(100vw-3rem))] max-w-[22rem] snap-center"
                >
                  <CounsellorCardBody counsellor={counsellor} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <CounsellorsMobileMarquee />
        )}

        <div className="text-center mt-12">
          <Link
            to="/counsellors"
            className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:text-teal-700 transition-colors"
          >
            View full profiles
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ExpertCounsellorsSection;